import { apiClient, API_CONFIG } from '@/api/core';
import type { ApiResponse, LC_PagedResult } from '@/api/shared';
import { buildQueryString } from '@/api/shared';

import {
  mapAttendanceConceptItem,
  mapSyncAttendanceConcepts,
  mapAttendanceConceptDetail,
  mapUpdateAttendanceConceptResult,
  mapCreateAttendanceConceptResult,
} from '../mappers/attendanceConcept.mapper';
import type {
  AttendanceConceptDetail,
  AttendanceConceptExportParams,
  AttendanceConceptListPage,
  AttendanceConceptListParams,
  CreateAttendanceConceptResult,
  LC_AttendanceConceptDetailOut,
  LC_AttendanceConceptItemOut,
  LC_CreateAttendanceConceptIn,
  LC_CreateAttendanceConceptOut,
  LC_SyncAttendanceConceptsOut,
  LC_UpdateAttendanceConceptIn,
  LC_UpdateAttendanceConceptOut,
  SyncAttendanceConceptsResult,
  UpdateAttendanceConceptResult,
} from '../types/attendanceConcept.types';


export class AttendanceConceptService {
  async list(
    params: AttendanceConceptListParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<AttendanceConceptListPage>> {
    const queryString = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: params.sort?.key,
      orderDirection: params.sort?.direction?.toUpperCase(),
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.ATTENDANCE_CONCEPTS.LIST}${queryString}`;

    const response = await apiClient.get<LC_PagedResult<LC_AttendanceConceptItemOut>>(
      endpoint,
      { signal },
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: {
          items: (response.data.data ?? []).map(mapAttendanceConceptItem),
          totalRecords: response.data.pagination.totalRecords,
        },
      };
    }

    return { success: false, data: null, error: response.error };
  }

  async sync(): Promise<ApiResponse<SyncAttendanceConceptsResult>> {
    const response = await apiClient.post<LC_SyncAttendanceConceptsOut>(
      API_CONFIG.ENDPOINTS.ATTENDANCE_CONCEPTS.SYNC,
      {},
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: mapSyncAttendanceConcepts(response.data),
      };
    }

    return { success: false, data: null, error: response.error };
  }
  
  async getById(
    id: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<AttendanceConceptDetail>> {
    const response = await apiClient.get<LC_AttendanceConceptDetailOut>(
      API_CONFIG.ENDPOINTS.ATTENDANCE_CONCEPTS.BY_ID(id),
      { signal },
    );

    if (response.success && response.data) {
      return { success: true, data: mapAttendanceConceptDetail(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }
  
  async update(
    id: number,
    body: LC_UpdateAttendanceConceptIn,
  ): Promise<ApiResponse<UpdateAttendanceConceptResult>> {
    const response = await apiClient.put<LC_UpdateAttendanceConceptOut>(
      API_CONFIG.ENDPOINTS.ATTENDANCE_CONCEPTS.BY_ID(id),
      body,
    );

    if (response.success && response.data) {
      return { success: true, data: mapUpdateAttendanceConceptResult(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  async create(
    body: LC_CreateAttendanceConceptIn,
  ): Promise<ApiResponse<CreateAttendanceConceptResult>> {
    const response = await apiClient.post<LC_CreateAttendanceConceptOut>(
      API_CONFIG.ENDPOINTS.ATTENDANCE_CONCEPTS.LIST,
      body,
    );

    if (response.success && response.data) {
      return { success: true, data: mapCreateAttendanceConceptResult(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  async export(
    params: AttendanceConceptExportParams = {},
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const queryString = buildQueryString({ searchText: params.searchText });
    const endpoint = `${API_CONFIG.ENDPOINTS.ATTENDANCE_CONCEPTS.FILE}${queryString}`;

    return apiClient.downloadBlobWithFilename(endpoint, { method: 'GET' });
  }

}

export const attendanceConceptService = new AttendanceConceptService();
